import BodyCopy from '@tcp/core/src/components/common/atoms/BodyCopy';
import Button from '@tcp/core/src/components/common/atoms/Button';
import Row from '@tcp/core/src/components/common/atoms/Row';
import Col from '@tcp/core/src/components/common/atoms/Col';
import GiftServices from '@tcp/core/src/components/features/CnC/Checkout/organisms/ShippingPage/molecules/GiftServices';
import PropTypes from 'prop-types';
import errorBoundary from '@tcp/core/src/components/common/hoc/withErrorBoundary/errorBoundary';
import WarningPrompt from '@tcp/core/src/components/features/CnC/Checkout/organisms/WarningPrompt';
import React from 'react';
import {
  getLabelFromCopyOrFromTranslation,
  renderLabel,
} from '@tcp/core/src/utils/labels/labelUtilty';
import CheckoutLabels from '@tcp/core/src/utils/labels/cnc/checkout.labels';
import {
  GLOBAL_ACTIONS_LABELS,
  GlobalActionsLabels,
} from '@tcp/core/src/utils/labels/globalActions.labels';
import ApiTriggerTracker from '@tcp/core/src/utils/apiTriggerLogger';
import {
  hasGiftServiceError,
  hasRushShippingError,
  getSfsErroMessage,
  getSaveForLaterItemsOrderItemId,
  hasApoFpoError,
  getSaveForLaterItemsProductIds,
} from '@tcp/core/src/components/features/CnC/Checkout/util/utility';
import { STANDARD_EDD_CODE } from '@tcp/core/src/components/features/CnC/common/molecules/EddComponent/EddComponent.constants';
import { addEditAddressLabelsObj } from '@tcp/core/src/components/features/CnC/SinglePageCheckout/molecules/ContactForm/ContactForm.labels.js';
import ShippingForm from '../../container/Shipping/ShippingForm.container';

const listenUpdateShipping = () => {
  try {
    const apiTriggerLoggerInstance = new ApiTriggerTracker();
    apiTriggerLoggerInstance.startListener('UPDATE_SHIPPING_DETAILS');
  } catch {
    console.error('apiTriggerLoggerInstance error with UPDATE_SHIPPING_DETAILS');
  }
};

const getSecondaryButtonType = (giftServiceError, rushShippingError, apoFpoError) => {
  let ctaType = 'shipingMethod';
  if (giftServiceError && !rushShippingError) {
    ctaType = 'giftservices';
  } else if (apoFpoError && !giftServiceError && !rushShippingError) {
    ctaType = 'shippingAddress';
  }
  return ctaType;
};

const renderShippingMethodErrors = ({
  sfsError,
  cartOrderItems,
  globalLabels,
  dispatchFormFieldChange,
  shipmentMethods,
  setSFSErrors,
  dispatchGiftFormFieldChange,
  addSFSItemsToSFL,
}) => {
  const checkoutLabels = getLabelFromCopyOrFromTranslation('checkout', CheckoutLabels);
  const standardShippingMethodsId = shipmentMethods?.find(
    (method) => method.code === STANDARD_EDD_CODE
  )?.id;

  const giftServiceError = sfsError && hasGiftServiceError(sfsError);
  const rushShippingError = sfsError && hasRushShippingError(sfsError);
  const apoFpoError = sfsError && hasApoFpoError(sfsError);
  if (giftServiceError || rushShippingError || apoFpoError) {
    const itemIds = getSaveForLaterItemsOrderItemId(sfsError) || [];
    const productIds = getSaveForLaterItemsProductIds(sfsError) || [];
    return (
      <WarningPrompt
        heading={renderLabel(
          checkoutLabels.shipping.lbl_sfs_cart_item_error,
          'lbl_sfs_cart_item_error'
        )}
        body={renderLabel(checkoutLabels.shipping[getSfsErroMessage(sfsError)])}
        type={getSecondaryButtonType(giftServiceError, rushShippingError, apoFpoError)}
        cartOrderItems={cartOrderItems}
        itemIds={itemIds}
        labels={globalLabels}
        saveForLaterAction={() => addSFSItemsToSFL(productIds)}
        secondaryCtaAction={() => {
          setSFSErrors(null);
          if (giftServiceError && !rushShippingError) {
            dispatchGiftFormFieldChange('hasGiftWrapping', false);
            dispatchGiftFormFieldChange(`message`, '');
          } else {
            dispatchFormFieldChange('shipmentMethods.shippingMethodId', standardShippingMethodsId);
          }
        }}
      />
    );
  }
  return null;
};

const ShippingExpanded = (props) => {
  const {
    formName,
    mode,
    savedAddress,
    handleSave,
    shipmentMethods,
    selectedShipmentId,
    loadShipmentMethods,
    updateShipmentMethods,
    updateSectionState,
    labels,
    giftServicesInput,
    savedGiftService,
    globalLabels,
    section,
    saveBtnLoadingStatus,
    orderEddDates,
    ShippingFormRef,
    sfsError,
    cartOrderItems,
    dispatchFormFieldChange,
    setSFSErrors,
    dispatchGiftFormFieldChange,
    addSFSItemsToSFL,
    isLoginV2FlowEnabled,
    isShake,
    isPlccIC,
    isMakeDefaultUxUpdateEnabled,
    makeDefaultAddress,
    defaultAddress,
    isDefaultChanged,
    className,
    orderTrackingEnhancementEnabled,
    isGcEnabledOnBagSection,
    smsCheckBoxState,
    openFormForVerification,
    setOpenFormForVerification,
  } = props;

  const addEditAddressLabels = getLabelFromCopyOrFromTranslation(
    `global.addEditAddress`,
    addEditAddressLabelsObj
  );

  const globalActionsLabels = getLabelFromCopyOrFromTranslation(
    GLOBAL_ACTIONS_LABELS,
    GlobalActionsLabels
  );
  const saveContinueCTA = isPlccIC
    ? renderLabel(globalActionsLabels?.lbl_save_caps, 'lbl_save_caps')
    : renderLabel(
        addEditAddressLabels.lbl_addEditAddress_saveAndContinue,
        'lbl_addEditAddress_saveAndContinue'
      );
  const handleSaveBtn = () => {
    listenUpdateShipping();
    handleSave();
  };
  const giftServiceError = sfsError && hasGiftServiceError(sfsError);

  return (
    <div className="spc-card-content" ref={ShippingFormRef}>
      <BodyCopy component="div">
        <ShippingForm
          formName={formName}
          savedAddress={savedAddress}
          mode={mode}
          isPlccIC={isPlccIC}
          updateSectionState={updateSectionState}
          handleSave={handleSave}
          shipmentMethods={shipmentMethods}
          selectedShipmentId={selectedShipmentId}
          loadShipmentMethods={loadShipmentMethods}
          updateShipmentMethods={updateShipmentMethods}
          labels={labels}
          giftServicesInput={giftServicesInput}
          globalLabels={globalLabels}
          section={section}
          orderEddDates={orderEddDates}
          isLoginV2FlowEnabled={isLoginV2FlowEnabled}
          isMakeDefaultUxUpdateEnabled={isMakeDefaultUxUpdateEnabled}
          makeDefaultAddress={makeDefaultAddress}
          defaultAddress={defaultAddress}
          isDefaultChanged={isDefaultChanged}
          className={className}
          orderTrackingEnhancementEnabled={orderTrackingEnhancementEnabled}
          smsCheckBoxState={smsCheckBoxState}
          openFormForVerification={openFormForVerification}
          setOpenFormForVerification={setOpenFormForVerification}
        />
        {section?.isMethodAndGiftSectionVisible && (
          <Row fullBleed>
            {!isGcEnabledOnBagSection && !isPlccIC && (
              <Col colSize={{ small: 6, medium: 8, large: 12 }} className="isGiftServicesChecked">
                <GiftServices
                  showDefaultCheckbox={false}
                  formName="shippingForm"
                  formSection="giftServices"
                  variation="secondary"
                  savedGiftService={savedGiftService}
                  spc
                />
              </Col>
            )}
            {(!isGcEnabledOnBagSection || (isGcEnabledOnBagSection && !giftServiceError)) &&
              renderShippingMethodErrors({
                sfsError,
                cartOrderItems,
                globalLabels,
                dispatchFormFieldChange,
                shipmentMethods,
                setSFSErrors,
                dispatchGiftFormFieldChange,
                addSFSItemsToSFL,
              })}
            <Col colSize={{ small: 6, medium: 4, large: 4.5 }}>
              <Button
                type="submit"
                buttonVariation="fixed-width"
                fill="BLUE"
                className={`shipping-save-btn btn-border ${isShake ? 'shake' : ''}`}
                disabled={false}
                onClick={handleSaveBtn}
                loading={saveBtnLoadingStatus}
                spc
              >
                {saveContinueCTA}
              </Button>
            </Col>
          </Row>
        )}
      </BodyCopy>
    </div>
  );
};

ShippingExpanded.propTypes = {
  formName: PropTypes.string.isRequired,
  mode: PropTypes.string.isRequired,
  updateSectionState: PropTypes.func.isRequired,
  isSaveDisabled: PropTypes.bool,
  handleSave: PropTypes.func.isRequired,
  addressFormLabels: PropTypes.shape({}).isRequired,
  savedAddress: PropTypes.shape({}),
  shipmentMethods: PropTypes.shape({}),
  selectedShipmentId: PropTypes.string,
  loadShipmentMethods: PropTypes.func.isRequired,
  updateShipmentMethods: PropTypes.func.isRequired,
  labels: PropTypes.shape({}).isRequired,
  giftServicesInput: PropTypes.shape({}).isRequired,
  savedGiftService: PropTypes.shape({}).isRequired,
  globalLabels: PropTypes.shape({}),
  section: PropTypes.shape({}).isRequired,
  saveBtnLoadingStatus: PropTypes.bool,
  orderEddDates: PropTypes.shape({}),
};

ShippingExpanded.defaultProps = {
  isSaveDisabled: false,
  savedAddress: {},
  shipmentMethods: {},
  selectedShipmentId: '',
  globalLabels: {},
  saveBtnLoadingStatus: false,
  orderEddDates: {},
};

export default errorBoundary(ShippingExpanded);
